import React from 'react';
import { getApiBaseFromWindow } from '../../../lib/runtimeConfig';
import { SpaceWorkSdrDashboard } from './SpaceWorkSdrDashboard';

type PanelId =
  | 'commercial'
  | 'sdr'
  | 'executive';

type Detail = {
  id: PanelId;
};

type CommercialMetrics = {
  salesToday: number;
  revenueToday: number;

  salesWeek: number;
  revenueWeek: number;

  salesMonth: number;
  revenueMonth: number;

  agendaToday: number;

  weeklyGoal: number;
};

type CommercialResponse = {
  ok: boolean;
  closerKey: string;
  closer: string;

  metrics: CommercialMetrics;

  updatedAt: string;
};

const CLOSERS = [
  'Matheus',
  'Luis',
  'Felipe',
] as const;

const copy: Record<
  PanelId,
  {
    title: string;
    subtitle: string;
    items: string[];
  }
> = {
  commercial: {
    title: 'Painel Comercial',
    subtitle: 'Operação de vendas',
    items: [],
  },

  sdr: {
    title: 'Painel SDR / Pré-Vendas',
    subtitle: 'Operação de prospecção',
    items: [
      'Ligações e atendidas',
      'Agendamentos',
      'Notas de IA',
    ],
  },

  executive: {
    title: 'Diretoria • Visão Geral',
    subtitle: 'Cockpit executivo',
    items: [
      'Comercial',
      'SDR',
      'CS / Retenção',
    ],
  },
};

const brl = (
  value: number,
) =>
  new Intl.NumberFormat(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0,
    },
  ).format(
    Number(value || 0),
  );

function MetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <div
      style={{
        padding: '14px 15px',
        borderRadius: 12,
        background: '#181d25',
        border: '1px solid #2a313d',
      }}
    >
      <div
        style={{
          color: '#8e98a6',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          color: '#fff',
          fontSize: 21,
          fontWeight: 900,
        }}
      >
        {value}
      </div>

      {hint ? (
        <div
          style={{
            marginTop: 4,
            color: '#747e8b',
            fontSize: 10,
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}


type SdrMetricBlock = {
  analyzedCalls: number;
  avgScore: number;
  avgDurationSeconds: number;
  criticalCalls: number;
};

type SdrPerson = {
  name: string;
  analyzedCalls: number;
  avgScore: number;
  avgDurationSeconds: number;
  criticalCalls: number;
};

type SdrMetricsResponse = {
  ok: boolean;
  today: SdrMetricBlock;
  week: SdrMetricBlock;
  sdrs: SdrPerson[];
  updatedAt: string;
};

function formatSdrDuration(
  seconds: number,
) {
  const value =
    Number(seconds || 0);

  const minutes =
    Math.floor(
      value / 60,
    );

  const rest =
    Math.round(
      value % 60,
    );

  return (
    `${minutes}m `
    + `${String(rest).padStart(2, '0')}s`
  );
}

function SpaceWorkSdrPanel({
  onClose,
}: {
  onClose: () => void;
}) {
  const [
    metrics,
    setMetrics,
  ] =
    React.useState<
      SdrMetricsResponse | null
    >(null);

  const [
    loading,
    setLoading,
  ] =
    React.useState(true);

  const [
    error,
    setError,
  ] =
    React.useState<
      string | null
    >(null);


  React.useEffect(() => {

    let alive = true;

    let controller:
      AbortController | null =
        null;


    const load =
      async () => {

        controller?.abort();

        controller =
          new AbortController();

        const timeout =
          window.setTimeout(
            () =>
              controller?.abort(),
            10000,
          );

        try {
          const apiBase =
            getApiBaseFromWindow();

          const response =
            await fetch(
              `${apiBase}/space-work/sdr-metrics`,
              {
                credentials:
                  'include',

                headers: {
                  Accept:
                    'application/json',
                },

                signal:
                  controller.signal,
              },
            );

          if (!response.ok) {
            throw new Error(
              `HTTP ${response.status}`,
            );
          }

          const payload =
            await response
              .json() as
              SdrMetricsResponse;

          if (
            !payload?.ok
            || !payload.today
            || !payload.week
            || !Array.isArray(
              payload.sdrs,
            )
          ) {
            throw new Error(
              'Resposta SDR inválida',
            );
          }

          if (!alive) {
            return;
          }

          setMetrics(
            payload,
          );

          setError(
            null,
          );

        } catch (
          loadError
        ) {
          if (!alive) {
            return;
          }

          setError(
            loadError
              instanceof Error
                ? loadError.message
                : 'Falha ao carregar SDR',
          );

        } finally {
          window.clearTimeout(
            timeout,
          );

          if (alive) {
            setLoading(false);
          }
        }
      };


    void load();


    const interval =
      window.setInterval(
        () => {
          void load();
        },
        30000,
      );


    return () => {
      alive = false;

      controller
        ?.abort();

      window.clearInterval(
        interval,
      );
    };

  }, []);


  const metricCard = (
    label: string,
    value: string | number,
    caption?: string,
    danger = false,
  ) => (
    <div
      style={{
        minWidth: 0,
        padding: '14px 15px',
        borderRadius: 12,
        background:
          '#171d25',
        border:
          danger
            ? '1px solid rgba(227,35,70,.45)'
            : '1px solid #2a313d',
      }}
    >
      <div
        style={{
          color:
            '#818b99',
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: 0.8,
          textTransform:
            'uppercase',
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          color:
            danger
              ? '#ff5873'
              : '#ffffff',
          fontSize: 24,
          lineHeight: 1,
          fontWeight: 900,
        }}
      >
        {value}
      </div>

      {caption ? (
        <div
          style={{
            marginTop: 7,
            color:
              '#707a88',
            fontSize: 10,
          }}
        >
          {caption}
        </div>
      ) : null}
    </div>
  );


  const sectionTitle = (
    text: string,
  ) => (
    <div
      style={{
        marginBottom: 10,
        color:
          '#8f99a7',
        fontSize: 10,
        fontWeight: 900,
        letterSpacing: 1.1,
      }}
    >
      {text}
    </div>
  );


  const updated =
    metrics?.updatedAt
      ? new Date(
          metrics.updatedAt,
        ).toLocaleTimeString(
          'pt-BR',
          {
            hour: '2-digit',
            minute: '2-digit',
          },
        )
      : '--';


  return (
    <div
      onMouseDown={
        event =>
          event.target
            === event.currentTarget
          &&
          onClose()
      }
      style={{
        position:
          'fixed',
        inset: 0,
        zIndex: 10000,

        display:
          'grid',
        placeItems:
          'center',

        padding: 20,

        background:
          'rgba(4,7,11,.74)',

        backdropFilter:
          'blur(8px)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width:
            'min(760px, calc(100vw - 32px))',

          maxHeight:
            'calc(100vh - 48px)',

          overflowY:
            'auto',

          color:
            '#fff',

          background:
            '#0f141b',

          border:
            '1px solid #2a313d',

          borderRadius:
            18,

          boxShadow:
            '0 30px 90px rgba(0,0,0,.55)',
        }}
      >
        <div
          style={{
            display:
              'flex',

            alignItems:
              'flex-start',

            justifyContent:
              'space-between',

            gap: 16,

            padding: 20,

            borderBottom:
              '1px solid #252b34',
          }}
        >
          <div>
            <div
              style={{
                color:
                  '#e32346',

                fontSize:
                  10,

                fontWeight:
                  900,

                letterSpacing:
                  1.3,
              }}
            >
              SPACE WORK
            </div>

            <div
              style={{
                marginTop:
                  5,

                fontSize:
                  22,

                fontWeight:
                  800,
              }}
            >
              Painel SDR / Pré-Vendas
            </div>

            <div
              style={{
                marginTop:
                  5,

                color:
                  '#929ba8',

                fontSize:
                  13,
              }}
            >
              Operação + Qualidade IA
            </div>
          </div>

          <button
            type="button"
            onClick={
              onClose
            }
            style={{
              width:
                34,

              height:
                34,

              flex:
                '0 0 auto',

              cursor:
                'pointer',

              color:
                '#fff',

              background:
                '#1b2028',

              border:
                '1px solid #303744',

              borderRadius:
                9,
            }}
          >
            ×
          </button>
        </div>


        <div
          style={{
            padding: 20,
          }}
        >
          {loading
            && !metrics
          ? (
            <div
              style={{
                padding:
                  '28px 0',

                color:
                  '#929ba8',

                textAlign:
                  'center',

                fontSize:
                  13,
              }}
            >
              Carregando métricas SDR...
            </div>
          )
          : null}


          {error
            && !metrics
          ? (
            <div
              style={{
                padding:
                  '14px 16px',

                borderRadius:
                  10,

                color:
                  '#ff7188',

                background:
                  'rgba(227,35,70,.08)',

                border:
                  '1px solid rgba(227,35,70,.28)',
              }}
            >
              Não foi possível carregar
              os dados SDR.
              {' '}
              {error}
            </div>
          )
          : null}


          {metrics ? (
            <>
              {error ? (
                <div
                  style={{
                    marginBottom:
                      14,

                    color:
                      '#ff9aac',

                    fontSize:
                      11,
                  }}
                >
                  Atualização falhou.
                  Mantendo os últimos
                  dados carregados.
                </div>
              ) : null}


              {sectionTitle(
                'HOJE',
              )}

              <div
                style={{
                  display:
                    'grid',

                  gridTemplateColumns:
                    'repeat(auto-fit, minmax(135px, 1fr))',

                  gap:
                    10,
                }}
              >
                {metricCard(
                  'Calls IA',
                  metrics.today
                    .analyzedCalls,
                  'gravações analisadas',
                )}

                {metricCard(
                  'Nota média',
                  Number(
                    metrics.today
                      .avgScore || 0,
                  ).toLocaleString(
                    'pt-BR',
                    {
                      maximumFractionDigits:
                        1,
                    },
                  ),
                  'score médio da IA',
                )}

                {metricCard(
                  'Críticas',
                  metrics.today
                    .criticalCalls,
                  'score abaixo de 60',
                  metrics.today
                    .criticalCalls > 0,
                )}

                {metricCard(
                  'Duração média',
                  formatSdrDuration(
                    metrics.today
                      .avgDurationSeconds,
                  ),
                  'por call analisada',
                )}
              </div>


              <div
                style={{
                  marginTop:
                    22,
                }}
              >
                {sectionTitle(
                  'SEMANA',
                )}

                <div
                  style={{
                    display:
                      'grid',

                    gridTemplateColumns:
                      'repeat(auto-fit, minmax(135px, 1fr))',

                    gap:
                      10,
                  }}
                >
                  {metricCard(
                    'Calls IA',
                    metrics.week
                      .analyzedCalls,
                    'gravações analisadas',
                  )}

                  {metricCard(
                    'Nota média',
                    Number(
                      metrics.week
                        .avgScore || 0,
                    ).toLocaleString(
                      'pt-BR',
                      {
                        maximumFractionDigits:
                          1,
                      },
                    ),
                    'score médio da IA',
                  )}

                  {metricCard(
                    'Críticas',
                    metrics.week
                      .criticalCalls,
                    'score abaixo de 60',
                    metrics.week
                      .criticalCalls > 0,
                  )}

                  {metricCard(
                    'Duração média',
                    formatSdrDuration(
                      metrics.week
                        .avgDurationSeconds,
                    ),
                    'por call analisada',
                  )}
                </div>
              </div>


              <div
                style={{
                  marginTop:
                    24,
                }}
              >
                {sectionTitle(
                  'PERFORMANCE POR SDR',
                )}

                <div
                  style={{
                    display:
                      'grid',

                    gap:
                      8,
                  }}
                >
                  {metrics.sdrs.map(
                    row => (
                      <div
                        key={
                          row.name
                        }
                        style={{
                          display:
                            'grid',

                          gridTemplateColumns:
                            'minmax(110px, 1.4fr) repeat(3, minmax(75px, 1fr))',

                          gap:
                            10,

                          alignItems:
                            'center',

                          padding:
                            '12px 14px',

                          borderRadius:
                            10,

                          background:
                            '#171d25',

                          border:
                            '1px solid #292f39',
                        }}
                      >
                        <div
                          style={{
                            minWidth:
                              0,

                            fontSize:
                              13,

                            fontWeight:
                              800,

                            overflow:
                              'hidden',

                            textOverflow:
                              'ellipsis',

                            whiteSpace:
                              'nowrap',
                          }}
                        >
                          {row.name}
                        </div>

                        <div>
                          <div
                            style={{
                              fontSize:
                                14,

                              fontWeight:
                                800,
                            }}
                          >
                            {
                              row
                                .analyzedCalls
                            }
                          </div>

                          <div
                            style={{
                              color:
                                '#717b88',

                              fontSize:
                                9,
                            }}
                          >
                            CALLS IA
                          </div>
                        </div>

                        <div>
                          <div
                            style={{
                              fontSize:
                                14,

                              fontWeight:
                                800,
                            }}
                          >
                            {Number(
                              row
                                .avgScore
                                || 0,
                            ).toLocaleString(
                              'pt-BR',
                              {
                                maximumFractionDigits:
                                  1,
                              },
                            )}
                          </div>

                          <div
                            style={{
                              color:
                                '#717b88',

                              fontSize:
                                9,
                            }}
                          >
                            NOTA IA
                          </div>
                        </div>

                        <div>
                          <div
                            style={{
                              color:
                                row
                                  .criticalCalls
                                  > 0
                                  ? '#ff5873'
                                  : '#ffffff',

                              fontSize:
                                14,

                              fontWeight:
                                800,
                            }}
                          >
                            {
                              row
                                .criticalCalls
                            }
                          </div>

                          <div
                            style={{
                              color:
                                '#717b88',

                              fontSize:
                                9,
                            }}
                          >
                            CRÍTICAS
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>


              <div
                style={{
                  display:
                    'flex',

                  flexWrap:
                    'wrap',

                  justifyContent:
                    'space-between',

                  gap:
                    8,

                  marginTop:
                    18,

                  paddingTop:
                    14,

                  borderTop:
                    '1px solid #252b34',

                  color:
                    '#6f7986',

                  fontSize:
                    10,
                }}
              >
                <span>
                  Calls IA = gravações
                  processadas pelo scoring.
                </span>

                <span>
                  Atualizado às
                  {' '}
                  {updated}
                  {' '}
                  • automático 30s
                </span>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}


export function SpaceWorkOperationsPanel() {
  const [
    panel,
    setPanel,
  ] =
    React.useState<PanelId | null>(
      null,
    );

  const [
    commercial,
    setCommercial,
  ] =
    React.useState<
      CommercialResponse[]
    >([]);

  const [
    loading,
    setLoading,
  ] =
    React.useState(false);

  const [
    error,
    setError,
  ] =
    React.useState<
      string | null
    >(null);


  React.useEffect(() => {

    const open = (
      event: Event,
    ) => {

      const id =
        (
          event as CustomEvent<Detail>
        ).detail?.id;

      if (id) {
        setPanel(id);
      }
    };


    const key = (
      event: KeyboardEvent,
    ) => {

      if (
        event.key === 'Escape'
      ) {
        setPanel(null);
      }
    };


    window.addEventListener(
      'space-work:open-panel',
      open as EventListener,
    );

    window.addEventListener(
      'keydown',
      key,
    );


    return () => {

      window.removeEventListener(
        'space-work:open-panel',
        open as EventListener,
      );

      window.removeEventListener(
        'keydown',
        key,
      );
    };

  }, []);


  React.useEffect(() => {

    if (
      panel !== 'commercial'
    ) {
      return;
    }

    let alive = true;

    let currentController:
      AbortController | null =
        null;


    const load = async () => {

      currentController?.abort();

      const controller =
        new AbortController();

      currentController =
        controller;

      const timer =
        window.setTimeout(
          () => controller.abort(),
          10000,
        );

      setLoading(true);


      try {

        const apiBase =
          getApiBaseFromWindow();

        const results =
          await Promise.allSettled(
            CLOSERS.map(
              async name => {

                const params =
                  new URLSearchParams({
                    name,
                    role: 'Closer',
                  });

                const response =
                  await fetch(
                    `${apiBase}/space-work/metrics?${params.toString()}`,
                    {
                      credentials:
                        'include',

                      headers: {
                        Accept:
                          'application/json',
                      },

                      signal:
                        controller.signal,
                    },
                  );


                if (!response.ok) {
                  throw new Error(
                    `${name}: HTTP ${response.status}`,
                  );
                }


                return (
                  await response.json()
                ) as CommercialResponse;
              },
            ),
          );


        if (!alive) {
          return;
        }


        const valid:
          CommercialResponse[] =
            [];


        for (
          const result
          of results
        ) {

          if (
            result.status
            === 'fulfilled'
            && result.value?.ok
          ) {
            valid.push(
              result.value,
            );
          }
        }


        setCommercial(
          valid,
        );


        if (
          valid.length === 0
        ) {

          setError(
            'Não foi possível carregar os dados comerciais.',
          );

        } else if (
          valid.length
          < CLOSERS.length
        ) {

          setError(
            `${valid.length} de ${CLOSERS.length} closers carregados. Os totais abaixo estão parciais.`,
          );

        } else {

          setError(null);
        }

      } catch (
        loadError
      ) {

        if (!alive) {
          return;
        }

        setError(
          loadError
            instanceof Error
              ? loadError.message
              : 'Falha ao carregar dados.',
        );

      } finally {

        window.clearTimeout(
          timer,
        );

        if (alive) {
          setLoading(false);
        }
      }
    };


    void load();


    const interval =
      window.setInterval(
        () => {
          void load();
        },
        30000,
      );


    return () => {

      alive = false;

      currentController
        ?.abort();

      window.clearInterval(
        interval,
      );
    };

  }, [panel]);


  const team =
    React.useMemo(
      () => {

        return commercial.reduce(
          (
            acc,
            row,
          ) => {

            const m =
              row.metrics;

            acc.salesToday +=
              Number(
                m.salesToday || 0,
              );

            acc.revenueToday +=
              Number(
                m.revenueToday || 0,
              );

            acc.salesWeek +=
              Number(
                m.salesWeek || 0,
              );

            acc.revenueWeek +=
              Number(
                m.revenueWeek || 0,
              );

            acc.salesMonth +=
              Number(
                m.salesMonth || 0,
              );

            acc.revenueMonth +=
              Number(
                m.revenueMonth || 0,
              );

            acc.agendaToday +=
              Number(
                m.agendaToday || 0,
              );

            acc.weeklyGoal +=
              Number(
                m.weeklyGoal || 0,
              );

            return acc;
          },
          {
            salesToday: 0,
            revenueToday: 0,

            salesWeek: 0,
            revenueWeek: 0,

            salesMonth: 0,
            revenueMonth: 0,

            agendaToday: 0,
            weeklyGoal: 0,
          },
        );

      },
      [commercial],
    );


  if (!panel) {
    return null;
  }


  

  

  if (panel === 'sdr') {
    return (
      <SpaceWorkSdrDashboard
        onClose={() => setPanel(null)}
      />
    );
  }
if (panel === 'sdr') {
    return (
      <SpaceWorkSdrPanel
        onClose={() => setPanel(null)}
      />
    );
  }
const data =
    copy[panel];

  const remaining =
    Math.max(
      team.weeklyGoal
        - team.revenueWeek,
      0,
    );

  const progress =
    team.weeklyGoal > 0
      ? Math.round(
          (
            team.revenueWeek
            /
            team.weeklyGoal
          )
          * 100,
        )
      : 0;


  return (
    <div
      onMouseDown={
        event =>
          event.target
            === event.currentTarget
          &&
          setPanel(null)
      }
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,

        display: 'grid',
        placeItems: 'center',

        background:
          'rgba(4,7,11,.74)',

        backdropFilter:
          'blur(8px)',

        padding: 20,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width:
            panel === 'commercial'
              ? 'min(760px, calc(100vw - 32px))'
              : 'min(560px, calc(100vw - 32px))',

          maxHeight:
            'calc(100vh - 48px)',

          overflowY:
            'auto',

          color: '#fff',
          background: '#0f141b',

          border:
            '1px solid #2a313d',

          borderRadius: 18,

          boxShadow:
            '0 30px 90px rgba(0,0,0,.55)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent:
              'space-between',

            padding: 20,

            borderBottom:
              '1px solid #252b34',
          }}
        >
          <div>

            <div
              style={{
                color: '#e32346',
                fontSize: 10,
                fontWeight: 900,
                letterSpacing: 1.3,
              }}
            >
              SPACE WORK
            </div>

            <div
              style={{
                marginTop: 5,
                fontSize: 22,
                fontWeight: 800,
              }}
            >
              {data.title}
            </div>

            <div
              style={{
                marginTop: 5,
                color: '#929ba8',
                fontSize: 13,
              }}
            >
              {panel
                === 'commercial'
                ? 'Operação comercial • dados reais • atualização automática'
                : data.subtitle}
            </div>
          </div>


          <button
            type="button"
            onClick={
              () => setPanel(null)
            }
            style={{
              width: 34,
              height: 34,

              cursor:
                'pointer',

              color:
                '#fff',

              background:
                '#1b2028',

              border:
                '1px solid #303744',

              borderRadius: 9,
            }}
          >
            ×
          </button>
        </div>


        <div
          style={{
            padding: 20,
          }}
        >
          {panel === 'commercial' ? (
            <>

              {loading
                && commercial.length === 0
                ? (
                  <div
                    style={{
                      padding: 24,
                      textAlign: 'center',
                      color: '#929ba8',
                    }}
                  >
                    Carregando operação comercial...
                  </div>
                )
                : null}


              {error ? (
                <div
                  style={{
                    marginBottom: 14,
                    padding: '10px 12px',

                    borderRadius: 10,

                    color: '#ffc6cf',

                    background:
                      'rgba(227,35,70,.10)',

                    border:
                      '1px solid rgba(227,35,70,.35)',

                    fontSize: 11,
                  }}
                >
                  {error}
                </div>
              ) : null}


              {commercial.length > 0 ? (
                <>

                  <div
                    style={{
                      color: '#929ba8',
                      fontSize: 11,
                      fontWeight: 800,
                      marginBottom: 9,
                    }}
                  >
                    HOJE
                  </div>


                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(3, minmax(0, 1fr))',
                      gap: 10,
                    }}
                  >
                    <MetricCard
                      label="Vendas"
                      value={
                        team.salesToday
                      }
                    />

                    <MetricCard
                      label="Receita"
                      value={
                        brl(
                          team.revenueToday,
                        )
                      }
                    />

                    <MetricCard
                      label="Agenda"
                      value={
                        team.agendaToday
                      }
                    />
                  </div>


                  <div
                    style={{
                      marginTop: 20,
                      color: '#929ba8',
                      fontSize: 11,
                      fontWeight: 800,
                      marginBottom: 9,
                    }}
                  >
                    SEMANA
                  </div>


                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(3, minmax(0, 1fr))',
                      gap: 10,
                    }}
                  >
                    <MetricCard
                      label="Vendas"
                      value={
                        team.salesWeek
                      }
                    />

                    <MetricCard
                      label="Receita"
                      value={
                        brl(
                          team.revenueWeek,
                        )
                      }
                    />

                    <MetricCard
                      label="Meta"
                      value={
                        brl(
                          team.weeklyGoal,
                        )
                      }
                    />

                    <MetricCard
                      label="Progresso"
                      value={
                        `${progress}%`
                      }
                    />

                    <MetricCard
                      label="Falta"
                      value={
                        brl(
                          remaining,
                        )
                      }
                    />

                    <MetricCard
                      label="Closers"
                      value={
                        `${commercial.length}/${CLOSERS.length}`
                      }
                    />
                  </div>


                  <div
                    style={{
                      marginTop: 20,
                      color: '#929ba8',
                      fontSize: 11,
                      fontWeight: 800,
                      marginBottom: 9,
                    }}
                  >
                    MÊS
                  </div>


                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(2, minmax(0, 1fr))',
                      gap: 10,
                    }}
                  >
                    <MetricCard
                      label="Vendas"
                      value={
                        team.salesMonth
                      }
                    />

                    <MetricCard
                      label="Receita"
                      value={
                        brl(
                          team.revenueMonth,
                        )
                      }
                    />
                  </div>


                  <div
                    style={{
                      marginTop: 20,
                      color: '#929ba8',
                      fontSize: 11,
                      fontWeight: 800,
                      marginBottom: 9,
                    }}
                  >
                    PERFORMANCE POR CLOSER
                  </div>


                  <div
                    style={{
                      display: 'grid',
                      gap: 8,
                    }}
                  >
                    {commercial.map(
                      row => (

                        <div
                          key={
                            row.closerKey
                          }
                          style={{
                            display:
                              'grid',

                            gridTemplateColumns:
                              '1.4fr .7fr 1fr 1fr',

                            alignItems:
                              'center',

                            gap: 10,

                            padding:
                              '11px 13px',

                            borderRadius:
                              10,

                            background:
                              '#181d25',

                            border:
                              '1px solid #2a313d',
                          }}
                        >
                          <div
                            style={{
                              fontWeight:
                                800,

                              fontSize:
                                12,
                            }}
                          >
                            {row.closer}
                          </div>

                          <div
                            style={{
                              fontSize:
                                11,

                              color:
                                '#aeb7c4',
                            }}
                          >
                            {
                              row.metrics
                                .salesWeek
                            } vendas
                          </div>

                          <div
                            style={{
                              fontSize:
                                11,

                              color:
                                '#aeb7c4',
                            }}
                          >
                            {brl(
                              row.metrics
                                .revenueWeek,
                            )}
                          </div>

                          <div
                            style={{
                              fontSize:
                                11,

                              color:
                                '#737d8a',
                            }}
                          >
                            mês{' '}
                            {brl(
                              row.metrics
                                .revenueMonth,
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </div>


                  <div
                    style={{
                      marginTop: 14,
                      color: '#66717f',
                      fontSize: 10,
                    }}
                  >
                    Atualização automática a cada 30 segundos.
                    Forecast não é exibido enquanto não houver
                    uma fonte confiável conectada.
                  </div>

                </>
              ) : null}

            </>
          ) : (
            <>

              {data.items.map(
                item => (
                  <div
                    key={item}
                    style={{
                      marginBottom: 9,

                      padding:
                        '12px 14px',

                      borderRadius:
                        10,

                      background:
                        '#181d25',

                      border:
                        '1px solid #2a313d',
                    }}
                  >
                    {item}
                  </div>
                ),
              )}

              <div
                style={{
                  marginTop: 16,
                  color: '#77808d',
                  fontSize: 11,
                }}
              >
                Próxima integração operacional.
              </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
}
